import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, ExternalLink } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const RISK_COLOR = { green: "#10B981", yellow: "#EAB308", red: "#EF4444" };

/**
 * Public-only report view served at /r/:id. Fetches from /api/public/reports/{id}
 * which never exposes originals, private author info, or unpublished content.
 * Renders "Apri nell'app" link that deep-links to /report/:id (handled by app if installed).
 */
export default function PublicReport() {
  const { id } = useParams();
  const { t } = useI18n();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/public/reports/${id}`).then((x) => setR(x.data)).catch((e) => setErr(e.response?.status || "err"));
  }, [id]);

  if (err) return (
    <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center p-6" data-testid="public-report-error">
      <div className="text-center">
        <div className="text-4xl mb-2">🌱</div>
        <div className="font-heading text-xl font-bold text-slate-900">{t("report_unavailable")}</div>
        <div className="text-sm text-slate-600 mt-1">Questa segnalazione potrebbe essere stata rimossa, resa privata o non ancora approvata.</div>
        <Link to="/" className="mt-4 inline-block text-emerald-700 hover:underline text-sm">← Torna a ToClean</Link>
      </div>
    </div>
  );
  if (!r) return <div className="p-10 text-slate-500">…</div>;

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="public-report-page">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="font-heading font-bold text-emerald-700">ToClean</Link>
          <Link data-testid="public-open-app" to={`/report/${id}`} className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1">
            Apri nell'app <ExternalLink size={11} />
          </Link>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="uppercase text-[10px]" style={{ background: RISK_COLOR[r.risk_level] || "#9CA3AF", color: "#fff" }}>{r.risk_level}</Badge>
            <Badge variant="outline">{r.category}</Badge>
            {r.subcategory && <Badge variant="outline">{r.subcategory.replace(/_/g, " ")}</Badge>}
            {/* Only show a status badge when it adds real information for the public
                (never expose raw technical states like "pending_moderation"). */}
            {r.status && !["published", "pending_moderation"].includes(r.status) && (
              <Badge variant="outline" className="capitalize">{r.status.replace(/_/g, " ")}</Badge>
            )}
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mt-2 break-words" data-testid="public-title">{r.title}</h1>
          <p className="mt-2 text-slate-700 text-sm break-words" data-testid="public-description">{r.description}</p>
          {(r.photos || []).length > 0 && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="public-photos">
              {(r.photos || []).slice(0, 4).map((p, i) => (
                <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="" className="w-full rounded-xl border border-slate-200" />
              ))}
            </div>
          )}
          <div className="mt-4 text-xs text-slate-500 flex items-center gap-1"><MapPin size={11} /> {r.province_name || "—"}, {r.country_name || "—"} · {r.confirmations || 0} conferme</div>
          <div className="mt-4">
            <Button data-testid="public-open-in-app-btn" asChild size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
              <Link to={`/report/${id}`}>{t("open_report_in_app")}</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
